/**
 * Utilidades para comparación de períodos en Estadísticas (Fase 2)
 */

import { parseLocalDate } from '../../../utils/formatters';
import type {
  ComparisonPeriod,
  MonthComparisonPoint,
  YearComparisonPoint,
} from '../../../types/statistics.types';

/** Re-export para imports desde este módulo */
export type { ComparisonPeriod } from '../../../types/statistics.types';

/** Nombres de meses en español (evita import de date-fns/locale para compatibilidad con Jest) */
const MONTH_NAMES_ES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

/** Obtiene from/to ISO para un mes específico (UTC para consistencia con API) */
export function getMonthRange(year: number, month: number): { from: string; to: string } {
  const start = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
  const end = new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)); // último día del mes
  return {
    from: start.toISOString(),
    to: end.toISOString(),
  };
}

/** Obtiene from/to ISO para un año completo (UTC para consistencia con API) */
export function getYearRange(year: number): { from: string; to: string } {
  const start = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
  const end = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
  return {
    from: start.toISOString(),
    to: end.toISOString(),
  };
}

/** Genera clave única para un período (ej. "2025-01" para mes, "2025" para año) */
export function getPeriodKey(period: ComparisonPeriod): string {
  if (period.month != null) {
    return `${period.year}-${String(period.month).padStart(2, '0')}`;
  }
  return String(period.year);
}

/** Etiqueta legible para un período (ej. "Ene 2025", "2025") */
export function getPeriodLabel(period: ComparisonPeriod, mode: 'months' | 'years'): string {
  if (mode === 'months' && period.month != null) {
    return `${MONTH_NAMES_ES[period.month - 1]} ${period.year}`;
  }
  return String(period.year);
}

/** Nombres cortos de meses para eje X en comparación de años */
const MONTH_LABELS = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

interface PeriodDataPoint {
  date: string;
  [key: string]: unknown;
}

/** Datos de un período con su clave */
export interface PeriodData<T extends PeriodDataPoint> {
  periodKey: string;
  periodLabel: string;
  data: T[];
}

/**
 * Normaliza datos diarios a estructura por día del mes (1-31).
 * Para comparación de meses: cada serie es un mes, X = día del mes.
 */
export function normalizeToDayOfMonth<T extends PeriodDataPoint>(
  periodData: PeriodData<T>[],
  getValue: (point: T) => number,
): MonthComparisonPoint[] {
  const result: MonthComparisonPoint[] = [];
  for (let day = 1; day <= 31; day++) {
    const point: MonthComparisonPoint = { dayOfMonth: day };
    for (const { periodKey, data } of periodData) {
      const found = data.find((p) => {
        const d = parseLocalDate(p.date);
        return !isNaN(d.getTime()) && d.getDate() === day;
      });
      point[periodKey] = found != null ? getValue(found as T) : 0;
    }
    result.push(point);
  }
  return result;
}

/**
 * Agrega datos diarios a mensual (12 puntos por año).
 * Para comparación de años: cada serie es un año, X = mes (1-12).
 */
export function aggregateDailyToMonthly<T extends PeriodDataPoint>(
  periodData: PeriodData<T>[],
  getValue: (point: T) => number,
): YearComparisonPoint[] {
  const result: YearComparisonPoint[] = [];
  for (let month = 1; month <= 12; month++) {
    const point: YearComparisonPoint = {
      month,
      monthLabel: MONTH_LABELS[month - 1],
    };
    for (const { periodKey, data } of periodData) {
      const monthData = data.filter((p) => {
        const d = parseLocalDate(p.date);
        return !isNaN(d.getTime()) && d.getMonth() + 1 === month;
      });
      const sum = monthData.reduce((acc, p) => acc + getValue(p as T), 0);
      point[periodKey] = sum;
    }
    result.push(point);
  }
  return result;
}

/** Lista de meses disponibles para selector (últimos 24 meses) */
export function getAvailableMonths(): ComparisonPeriod[] {
  const result: ComparisonPeriod[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    result.push({ year: d.getFullYear(), month: d.getMonth() + 1 }); // month 1-12
  }
  return result;
}

/** Lista de años disponibles para selector (últimos 10 años) */
export function getAvailableYears(): number[] {
  const result: number[] = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 9; y--) {
    result.push(y);
  }
  return result;
}

/** Parsea clave de mes "YYYY-MM" a ComparisonPeriod */
export function parseMonthKey(key: string): ComparisonPeriod | null {
  const m = /^(\d{4})-(\d{1,2})$/.exec(key);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (month < 1 || month > 12) return null;
  return { year, month };
}

/** Parsea clave de año "YYYY" a ComparisonPeriod */
export function parseYearKey(key: string): ComparisonPeriod | null {
  const m = /^(\d{4})$/.exec(key);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  if (year < 2000 || year > 2100) return null;
  return { year };
}
